import tinyColor from 'tinycolor2';

// As the Highcharts gauge: never less than a sliver, so a gauge at the
// bottom of its peer group still shows where it is
const MINIMUM_FILL = 10;

// A gauge's colour for how far round it fills: danger up to a third, warning
// up to two thirds, success above. Solid bands, as the Highcharts gauge draws
// them, unless it's a gradient, which blends between them.
export const toGaugeColor = ({
  fill,
  theme,
  isGradient = false,
}: {
  fill: number;
  theme: StudioCharts.Theme;
  isGradient?: boolean;
}): string => {
  const stops: [number, string][] = [
    [33, theme.danger],
    [66, theme.warning],
    [100, theme.success],
  ];
  if (!isGradient) {
    const band = stops.find(([at]) => fill <= at);
    return band ? band[1] : theme.success;
  }
  if (fill <= stops[0][0]) {
    return stops[0][1];
  }
  const upper = stops.findIndex(([at]) => fill <= at);
  if (upper === -1) {
    return stops[stops.length - 1][1];
  }
  const [fromAt, from] = stops[upper - 1];
  const [toAt, to] = stops[upper];
  if (fill === toAt) {
    return to;
  }
  return tinyColor
    .mix(from, to, ((fill - fromAt) / (toAt - fromAt)) * 100)
    .toHexString();
};

// A gauge: its metric's value for one entity, filled as far as its
// percentile among its peer group (100 the best, whichever way the gauge
// runs), its ends the group's worst and best values (the other way round
// where lower is better; see isGaugeLowerBetter)
export const toGaugeModel = ({
  gauge,
  data,
  theme,
  formatMetric,
  entityLabel,
  isLowerBetter,
  isGradient,
}: {
  gauge: VisualisationDefinitions.Gauge;
  isLowerBetter: boolean;
  data: { percentile: number; value: number; min: number; max: number };
  theme: StudioCharts.Theme;
  formatMetric: (args: {
    metricId: string;
    value: string | number | null | undefined;
  }) => string;
  // Whose value it is, where the gauge's entity is picked on the page
  entityLabel?: string;
  // Its colour blends between the bands, rather than stepping through them
  isGradient?: boolean;
}): StudioCharts.GaugeModel => {
  const fill = Math.max(data.percentile, MINIMUM_FILL);
  const format = (value: number) =>
    formatMetric({ metricId: gauge.metricId, value });
  const [worst, best] = isLowerBetter
    ? [data.max, data.min]
    : [data.min, data.max];
  return {
    kind: 'gauge',
    seriesId: `${gauge.metricId}:gauge`,
    metricId: gauge.metricId,
    name: gauge.name,
    fill,
    color: toGaugeColor({ fill, theme, isGradient }),
    valueLabel: format(data.value),
    startLabel: format(worst),
    endLabel: format(best),
    entityLabel:
      gauge.entity !== 'Dynamic' ? gauge.entity : entityLabel || undefined,
    showLegend: false,
    isNoData: false,
  };
};
